import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/use-toast';
import { useRestaurant } from '../../context/RestaurantContext';
import { ChevronLeft } from 'lucide-react';
import { getPurchase } from '../../services/purchaseService';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Read-only view of one purchase and its lines. */
export default function PurchaseDetail() {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId, purchaseId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const { toast } = useToast();

  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId || !locationId || !purchaseId) return;
    setLoading(true);
    try {
      const data = await getPurchase(restaurantId, locationId, purchaseId);
      setPurchase(data);
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to load the purchase', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, purchaseId, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 w-full">
      <button
        onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/purchase`)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" /> Purchases
      </button>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : !purchase ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Purchase not found.</CardContent></Card>
      ) : (
        <>
          <div className="mb-6">
            <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight">Invoice {purchase.invoice_number}</h1>
          </div>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Details</CardTitle>
              <CardDescription className="capitalize">{purchase.purchase_type} purchase</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3 text-sm">
              <div><div className="text-muted-foreground">Date</div><div>{fmtDate(purchase.purchase_date)}</div></div>
              <div><div className="text-muted-foreground">Supplier</div><div>{purchase.supplier || '—'}</div></div>
              <div><div className="text-muted-foreground">Total</div><div className="font-semibold tabular-nums">{fmtMoney(purchase.total_cost)}</div></div>
              {purchase.note && <div className="sm:col-span-3"><div className="text-muted-foreground">Note</div><div>{purchase.note}</div></div>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Items</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Variant</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit cost</TableHead>
                      <TableHead className="text-right">Line total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(purchase.lines || []).map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="font-medium">{l.dish_name}</TableCell>
                        <TableCell>{l.variant_name || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtMoney(l.unit_cost)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtMoney(l.line_cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
